/* global ApplePaySession */
import type {TApplePaySafeChargeConfig} from './types/TApplePayAPI';
import type {ApplePayJS, ApplePayError} from './types/TApplePayWebAPI';
import ManagerRepository from 'coreClasses/ManagerRepository';

type TApplePayBackendResponse = Record<string, any> & {
	redirect?: string;
	error?: string;
	total?: ApplePayJS.ApplePayLineItem;
	lineItems?: ApplePayJS.ApplePayLineItem[];
	shippingMethods?: ApplePayJS.ApplePayShippingMethod[];
	event?: {detail?: Record<string, any>};
};

interface ISafeChargePaymentScript {
	applePay: {
		buildSession: (
			request: Record<string, any>,
			callback: (payResult: Record<string, any>, completion: (success: boolean, errors?: ApplePayError[]) => void) => void,
		) => IApplePaySession;
	};
}

interface IApplePaySession {
	oncancel: () => void;
	onpaymentmethodselected: (event: Event) => void;
	onshippingmethodselected: (event: Event) => void;
	onshippingcontactselected: (event: Event) => void;
	completePaymentMethodSelection: (update: ApplePayJS.ApplePayPaymentMethodUpdate) => void;
	completeShippingContactSelection: (update: ApplePayJS.ApplePayShippingContactUpdate) => void;
	completeShippingMethodSelection: (update: ApplePayJS.ApplePayShippingMethodUpdate) => void;
	begin: () => void;
	abort: () => void;
}

// TODO: Do we need to support applePay v2?
const APPLE_PAY_API_SUPPORTED = {
	MIN: 3,
	MAX: 4,
};

const processorConfig = {
	paymentScript: 'sfc',
	requiredData: 'merchantSiteId',
	data: ['merchantSiteId', 'env'],
};

/**
 * Represents ApplePayProcessor
 * Old class for SafeCharge payment processor
 */
class ApplePayProcessor {
	API_VERSION: number;
	handlePaymentAuthorized: (url: string) => void;
	handleError: (error: Error) => void;
	handleCancel: () => void;
	private errors: ApplePayError[];
	private config: TApplePaySafeChargeConfig;
	private staticRequestFields: Record<string, any>;
	private requiredRequestField: string;
	private request: Record<string, any> = {};
	private paymentScript: ISafeChargePaymentScript;
	private session: IApplePaySession | null = null;
	private isDebug: boolean;
	private managerRepository: ManagerRepository;

	/**
	 * @constructor
	 * @param {ManagerRepository} managerRepository - manager repository
	 * @param {TApplePaySafeChargeConfig} config - ApplePay configuration
	 * @param {boolean} isDebug - is debugging mode enabled
	 */
	constructor(managerRepository: ManagerRepository, config: TApplePaySafeChargeConfig, isDebug: boolean = false) {
		this.config = config;
		this.errors = [];
		this.API_VERSION = this.detectSupportedVersion();
		this.staticRequestFields = this.getStaticRequestFields();
		this.isDebug = isDebug;
		this.managerRepository = managerRepository;
	}

	/**
	 * Returns static request fields configured in the backend.
	 * @returns {Object} static request fields
	 */
	private getStaticRequestFields() {
		const result = {};

		this.requiredRequestField = processorConfig.requiredData;
		this.paymentScript = (window as Record<string, any>)[processorConfig.paymentScript];

		for (let i = 0; i < processorConfig.data.length; i++) {
			let requestField = processorConfig.data[i];

			if (this.config[requestField]) {
				result[requestField] = this.config[requestField];
			}
		}

		return result;
	}

	/**
	 * Initializes request object.
	 */
	private initializeRequest() {
		this.request = Object.assign(this.request || {}, this.staticRequestFields);
	}

	/**
	 * Detects supported version of Apple Pay API.
	 * @returns {number} API version
	 */
	private detectSupportedVersion() {
		for (let apVersion = APPLE_PAY_API_SUPPORTED.MAX; apVersion >= APPLE_PAY_API_SUPPORTED.MIN; apVersion--) {
			if (ApplePaySession.supportsVersion(apVersion)) {
				return apVersion;
			}
		}

		return null;
	}

	/**
	 * Processes validation errors for contact data.
	 * @param contact contact object from ApplePay response
	 * @param errorSection ApplePay section
	 * @returns {Array<ApplePayError>} array of errors
	 */
	private handleContactValidation(contact, errorSection) {
		let errors = [];

		if (!contact.valid) {
			const shippingContactFields = Object.keys(contact.fields);

			shippingContactFields.map((field) => {
				const fieldObject = contact.fields[field];
				const fieldKeys = Object.keys(fieldObject);
				const message = fieldKeys.filter((key) => fieldObject[key].errorMessage);

				errors.push(new ApplePayError(
					errorSection as ApplePayJS.ApplePayErrorCode,
					field as ApplePayJS.ApplePayErrorContactField,
					message as unknown as string,
				));
			});
		}

		return errors;
	}

	/**
	 * Creates new ApplePay session.
	 */
	private createSession() {
		this.session = this.paymentScript.applePay.buildSession(
			this.request,
			function (payResult, completion) {
				this.showDebugMessage('payResult', payResult);

				this.managerRepository
					.getSfccAPI()
					.applePayValidation(payResult)
					.then(({validationResult}) => {
						this.showDebugMessage('applePayValidation - result', validationResult);

						let errors = [];

						if (!validationResult.shippingContact.valid) {
							errors = errors.concat(
								this.handleContactValidation(
									validationResult.shippingContact,
									'shippingContactInvalid',
								),
							);
						}

						if (!validationResult.billingContact.valid) {
							errors = errors.concat(
								this.handleContactValidation(validationResult.billingContact, 'billingContactInvalid'),
							);
						}

						if (errors.length > 0) {
							completion(false, errors);
						} else {
							this.onPaymentAuthorized(payResult, completion);
						}
					})
					.catch((error) => {
						this.session.abort();
						this.handleError(error);
					});
			}.bind(this),
		);

		this.session.oncancel = this.onCancelHandler.bind(this);
		this.session.onshippingcontactselected = this.onShippingContactSelected.bind(this);
		this.session.onpaymentmethodselected = this.onPaymentMethodSelected.bind(this);
		this.session.onshippingmethodselected = this.onShippingMethodSelected.bind(this);

		this.session.begin();
	}

	/**
	 * Initializes ApplePay processor.
	 * @param setIsInitialized {Function} callback function to set isInitialized flag
	 */
	initialize(setIsInitialized) {
		this.getRequest().then(() => setIsInitialized(true));
	}

	/**
	 * Processes ApplePay request.
	 * @param productSku {String} product ID if ApplePay is used for single product
	 */
	applePayHandler(productSku) {
		this.initializeRequest();

		if (productSku) {
			this.prepareBasket(productSku);
		}

		this.createSession();
	}

	/**
	 * Does initial request to Backend to get request object for ApplePay.
	 * @returns {Promise<any>}
	 */
	private getRequest() {
		this.initializeRequest();

		return this.managerRepository
			.getSfccAPI()
			.applePayGetRequest()
			.then((response) => {
				this.request = Object.assign(this.request || {}, response.request);
			})
			.catch((err) => {
				this.handleError(err);
			});
	}

	/**
	 * Executes call to Backend to get shipping methods
	 * @param eventObj {Object} event object
	 * @returns {Promise<any>}
	 */
	private onShippingContactSelected(eventObj) {
		return this.managerRepository
			.getSfccAPI()
			.applePayOnShippingContactSelected(eventObj)
			.then((response) => {
				if (this.session) {
					this.completeShippingContactSelection(response);
				}
			})
			.catch((err) => {
				this.handleError(err);
			});
	}

	/**
	 * Updates ApplePay session with shipping methods.
	 * @param response {Object} Response object from Backend
	 */
	private completeShippingContactSelection(response: TApplePayBackendResponse) {
		this.request = Object.assign(this.request || {}, response);
		this.showDebugMessage('onShippingContactSelected - result', response);

		const errors = this.getApplePayDataFromResponse(response);
		this.errors.concat(errors);

		const completeObject: ApplePayJS.ApplePayShippingContactUpdate = {
			newTotal: (response.total || {}) as ApplePayJS.ApplePayLineItem,
			newLineItems: response.lineItems || [],
			newShippingMethods: response.shippingMethods || [],
			errors: errors || [],
		};

		this.showDebugMessage('onShippingContactSelected - completeObject', completeObject);

		this.session.completeShippingContactSelection(completeObject);
	}

	/**
	 * Extracts errors data from Backend response.
	 * @param response {Object} Response object from Backend
	 * @returns {Array<ApplePayError>} array of errors
	 */
	private getApplePayDataFromResponse(response: TApplePayBackendResponse) {
		const eventData = response?.event?.detail;
		const errors = [];

		if (eventData?.errorFields?.length > 0) {
			eventData.errorFields.map((errorField, index) => {
				errors.push(new ApplePayError(eventData.errorSection, errorField, eventData.errorMessages[index]));
			});
		}

		return errors;
	}

	/**
	 * Executes call to Backend with selected shipping method.
	 * @param eventObj {Object} event object
	 * @returns {Promise<any>}
	 */
	private onShippingMethodSelected(eventObj) {
		this.showDebugMessage('onShippingMethodSelected', eventObj);

		return this.managerRepository
			.getSfccAPI()
			.applePayOnShippingMethodSelected(eventObj)
			.then((response) => {
				if (this.session) {
					this.completeShippingMethodSelection(response);
				}
			})
			.catch((err) => {
				this.handleError(err);
			});
	}

	/**
	 * Updates ApplePay session with updated data after shipping method selection.
	 * @param response {Object} Response object from Backend
	 */
	private completeShippingMethodSelection(response: TApplePayBackendResponse) {
		this.showDebugMessage('onShippingMethodSelected - response', response);

		let completeObject: ApplePayJS.ApplePayShippingMethodUpdate = {
			newTotal: this.request.total,
		};

		if (!('error' in response)) {
			completeObject = {
				newTotal: response.total,
				newLineItems: response.lineItems,
			};

			this.request = Object.assign(this.request || {}, response);
		}

		this.showDebugMessage('onShippingMethodSelected - completeObject', completeObject);
		this.session.completeShippingMethodSelection(completeObject);
	}

	/**
	 * Executes call to Backend with selected payment method.
	 * @param eventObj {Object} event object
	 * @returns {Promise<any>}
	 */
	private onPaymentMethodSelected(eventObj) {
		this.showDebugMessage('onPaymentMethodSelected', eventObj);

		return this.managerRepository
			.getSfccAPI()
			.applePayOnPaymentMethodSelected(eventObj)
			.then((response: TApplePayBackendResponse) => {
				if (response.redirect) {
					this.onCancelHandler().then(() => {
						window.location.href = response.redirect;
					});

				} else if (this.session) {
					this.completePaymentMethodSelection(response);
				}
			})
			.catch((err) => {
				this.handleError(err);
			});
	}

	/**
	 * Updates ApplePay session after payment method selection.
	 * @param response {Object} Response object from Backend
	 */
	private completePaymentMethodSelection(response: TApplePayBackendResponse) {
		this.showDebugMessage('onPaymentMethodSelected - result', response);

		const errors = this.getApplePayDataFromResponse(response);
		this.errors.concat(errors);

		const completeObject: ApplePayJS.ApplePayPaymentMethodUpdate = {
			newTotal: (response.total || {}) as ApplePayJS.ApplePayLineItem,
			newLineItems: response.lineItems || [],
			errors: errors || [],
		};

		this.showDebugMessage('onPaymentMethodSelected - completeObject', completeObject);
		this.session.completePaymentMethodSelection(completeObject);
	}

	/**
	 * Executes call to Backend to create a new basket for selected product.
	 * @param productSku {String} product ID if ApplePay is used for single product
	 * @returns {Promise<any>}
	 */
	private prepareBasket(productSku) {
		return this.managerRepository
			.getSfccAPI()
			.applePayPrepareBasket(productSku)
			.catch((error) => {
				if (this.session) {
					this.session.abort();
				}

				this.handleError(error);
			});
	}

	/**
	 * Executes call to Backend to cancel ApplePay session.
	 * @returns {Promise<any>}
	 */
	private onCancelHandler() {
		this.session = null;

		return this.managerRepository
			.getSfccAPI()
			.applePayCancel()
			.then(() => this.handleCancel())
			.catch((error) => this.handleError(error));
	}

	/**
	 * Executes call to Backend to complete ApplePay session.
	 * @param eventObj {Object} event object
	 * @param completion {Function} Callback function
	 * @returns {Promise<any>}
	 */
	private onPaymentAuthorized(eventObj, completion) {
		this.showDebugMessage('onPaymentAuthorized', eventObj);

		return this.managerRepository
			.getSfccAPI()
			.applePayOnPaymentAuthorized({payment: eventObj})
			.then((response: TApplePayBackendResponse) => {
				this.showDebugMessage('onPaymentAuthorized - done', response);

				if (!response.error) {
					this.handlePaymentAuthorized(response.redirect);
					completion(true);
				} else {
					this.handleError(new Error(response.error));
					completion(false);
				}
			})
			.catch((err) => {
				this.handleError(err);
				completion(false);
			});
	}

	/**
	 * Adds console.log if debug mode is enabled.
	 * @param section {String} section name
	 * @param obj {Object} object with debug info
	 */
	private showDebugMessage(section, obj) {
		if (this.isDebug) {
			console.info(section, obj);
		}
	}
}

export default ApplePayProcessor;
